import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, CustomRadioButton, Default_Text_Color } from '../../utils/globalImports';
import { CustomButton, CustomTextField, validationMessages, ArrowBackIosIcon } from '../../utils/globalImports';

interface FeeData {
  feeName: string;
  description: string;
  status: string;
}

interface CustomAddFeeStageModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FeeData, setBackendErrors?: (errors: any) => void) => void;
  feeData?: FeeData; 
  handleContentChange:(state:boolean)=>void
}

const validationMessageStyle = {
  color: "rgba(255, 0, 0, 0.99)",
  fontSize: "11px",
  paddingLeft: "10px",
  paddingBottom: "10px",
};

const CustomAddFeeStageAreaModal: React.FC<CustomAddFeeStageModalProps> = ({ open, onClose, onSubmit, feeData,handleContentChange }) => {
  const [formData, setFormData] = useState<FeeData>({
    feeName: '',
    description: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState({
    feeName: '',
    description: '',
  });

  const [backendErrors, setBackendErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    let isValid = true;
    const newErrors = { feeName: '', description: '' };

    if (!formData.feeName.trim()) {
      newErrors.feeName = validationMessages.common_required("Fee Stage Area Name");
      isValid = false;
    } else if (formData.feeName.length > 255) {
      newErrors.feeName = validationMessages.common_characterLimit("Fee Stage Area Name", 255);
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(formData.feeName)) { // Regex to check for alphabets and spaces
      newErrors.feeName = validationMessages.common_invalidString("Fee Stage Area Name");
      isValid = false;
    }

    if (formData.description.length > 1000) {
      newErrors.description = validationMessages.common_characterLimit("Description", 1000);
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    if (open) {
      if (feeData) {
        setFormData({
          feeName: feeData.feeName || '',
          description: feeData.description || '',
          status: feeData.status || 'Active',
        });
      } else {
        setFormData({
          feeName: '',
          description: '',
          status: 'Active',
        });
      }
      setErrors({
        feeName: '', description: ''
      });
      setBackendErrors({});
    }
  }, [open, feeData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    handleContentChange(true);
  };

  const handleSubmit = async () => {
    if (validate()) {
      setBackendErrors({});
      await onSubmit(formData, setBackendErrors);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 350,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 500,
          height: "100%",
          padding: 2,
          backgroundColor: "rgba(255, 255, 255, 0.86)",
        },
      }}
    >
      <Box>
        <Typography 
            sx={{  
              color: Default_Text_Color,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              mt: 2,
              position: 'relative',
              fontWeight: 500,
              fontSize: 22 
            }}>
          <IconButton sx={{ position: "absolute", left: 0 }} onClick={onClose}>
            <ArrowBackIosIcon sx={{ color: "#1e66ab", fontSize: "15px" }} />
          </IconButton>
          {feeData ? "Edit Fee Stage Area" : "Add Fee Stage Area"}
        </Typography>

        <Divider sx={{mb: 3, mx: "-15px"  }} />

        <CustomTextField label="Fee Stage Area Name" name="feeName" value={formData.feeName} onChange={handleChange} sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }} required />
        {errors.feeName && <Typography style={validationMessageStyle}>{errors.feeName}</Typography>}
        {backendErrors.name && <Typography style={validationMessageStyle}>{backendErrors.name}</Typography>}

        <CustomTextField label="Description" name="description" value={formData.description} onChange={handleChange} sx={{
          mb: 2, mt:1.5, backgroundColor: '#fff',
          '& .MuiInputBase-root': {
            height: 'auto',  // Make sure that the outer container can auto adjust its height
          },
          '& .MuiOutlinedInput-input': {
            minHeight: 100, // Set the minimum height of the input field (works for textareas as well)
            padding: '10px', // Add padding to avoid text sticking to the edges
          },
        }} isTextArea />
        {errors.description && <Typography style={validationMessageStyle}>{errors.description}</Typography>}

        <Typography sx={{ color: "#1e66ab", fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
        <Box sx={{ display: "flex" }}>
          <CustomRadioButton label="Active" value="Active" name="status" checked={formData.status === 'Active'} onChange={handleChange} required />
          <CustomRadioButton label="Inactive" value="Inactive" name="status" checked={formData.status === 'Inactive'} onChange={handleChange} required />
        </Box>

        <DialogActions>
          <CustomButton capsule sx={{
              textTransform: 'none',
              width: '282px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              mr: 'auto',
              ml: 'auto',
              mt: 2,
            }} onClick={handleSubmit}>{feeData ? "Update Fee Stage Area" : "Add Fee Stage Area"}</CustomButton>
        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddFeeStageAreaModal;